import React, { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Link, useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/i18n/LanguageContext';
import { BookService } from '../services/bookService';
import { Skeleton } from '@/components/ui/skeleton';
import { ThemeSection } from './ThemeSection';
import { Theme, ThemeSelectionDialog } from './ThemeSelectionDialog';
import { FullscreenBookEditDialog } from './FullscreenBookEditDialog';
import { BookContentDisplay } from './DraggableBookContent';

interface Props {
  bookId: string;
}

// Define Book interface to match new BookSection structure
interface BookData {
  id: string;
  title: string;
  name?: string;
  description: string;
  category: string;
  duration: string;
  trainingType: string;
  status: string;
  certification: boolean;
  createdDate: string;
  lastModified: string;
  contents: BookSection[]; // Root sections (chapters)
  // Optional fields that might be in API but not in seeder
  createdBy?: string;
  author?: string;
  createdAt?: string;
  updatedAt?: string;
  level?: string;
  book_type?: string;
}

interface BookSection {
  id: string;
  name: string;
  title?: string;
  order: number;
  type: 'chapter' | 'file' | 'content' | 'text' | 'image' | 'pdf' | 'video' | 'audio' | 'interactive' | 'quiz';
  duration: number;
  start_day_offset: number;
  start_day_offset_relative_to: 'book' | 'chapter' | 'section';
  start_day_offset_reference_id?: string;
  file_id?: string;
  file?: {
    file_id: string;
    file_name: string;
    file_type: string;
    file_size: number;
    file_url: string;
  };
  children?: BookSection[]; // Child sections
}

export function BookContentDetail({ bookId }: Props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [book, setBook] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  
  // Get edit dialog state from URL params
  const isEditDialogOpen = searchParams.get('editContent') === 'true';
  
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const { t } = useLanguage();

  // Function to update URL params without full navigation
  const updateSearchParams = useCallback((updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Function to open edit dialog
  const openEditDialog = () => {
    updateSearchParams({ editContent: 'true' });
  };

  // Function to close edit dialog
  const closeEditDialog = useCallback(() => {
    updateSearchParams({ editContent: null });
  }, [updateSearchParams]);

  // Handle browser back button when dialog is open
  useEffect(() => {
    const handlePopState = () => {
      // If the edit param was removed from URL (e.g., back button), ensure dialog closes
      if (searchParams.get('editContent') !== 'true' && isEditDialogOpen) {
        // The dialog will automatically close since isEditDialogOpen is derived from URL
      }
    };

    // Handle escape key to close dialog
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isEditDialogOpen) {
        closeEditDialog();
      }
    };

    window.addEventListener('popstate', handlePopState);
    document.addEventListener('keydown', handleEscapeKey);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [searchParams, isEditDialogOpen, closeEditDialog]);

  // Helper function to translate delivery methods to Vietnamese
  const getDeliveryMethodLabel = (method: string) => {
    switch (method) {
      case 'combined': return 'Học tập tổng hợp';
      case 'e-learning': return 'Học trực tuyến';
      case 'live-online': return 'Học trực tiếp online';
      case 'in-person': return 'Học trực tiếp';
      case 'offline': return 'Học tại lớp';
      case 'file': return 'Tài liệu';
      case 'practical': return 'Thực hành';
      case 'quiz': return 'Bài kiểm tra';
      default: return method;
    }
  };

  // Helper function to translate module types to Vietnamese
  const getModuleTypeLabel = (type: string) => {
    switch (type) {
      case 'combined': return 'Tổng hợp';
      case 'e-learning': return 'Trực tuyến';
      case 'live-online': return 'Trực tiếp online';
      case 'in-person': return 'Trực tiếp';
      case 'offline': return 'Tại lớp';
      default: return type;
    }
  };

  // Helper function to get delivery method styling
  const getDeliveryMethodStyle = (method: string) => {
    switch (method) {
      case 'combined': 
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'e-learning': 
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'live-online': 
        return 'bg-green-100 text-green-800 border-green-200';
      case 'in-person': 
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offline': 
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'file': 
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'practical': 
        return 'bg-red-100 text-red-800 border-red-200';
      case 'quiz': 
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: 
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  // Helper function to get delivery method icon
  const getDeliveryMethodIcon = (method: string) => {
    switch (method) {
      case 'combined':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
        );
      case 'e-learning':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'live-online':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        );
      case 'in-person':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
      case 'offline':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        );
      case 'file':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
      case 'practical':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        );
      case 'quiz':
        return (
          <svg className="w-3 h-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6h18"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12h10"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 18h6"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6v12a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2"></path>
          </svg>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    const fetchBook = async () => {
      setLoading(true);
      try {
        const response = await BookService.getBookDetails(bookId);
        const data = response.data || response;
        
        setBook(data);
        
        
        
        // Set default expanded state for all contents and sections
        if (data.contents && data.contents.length > 0) {
          const chapterIds = new Set<string>();
          data.contents.forEach((chapter: BookSection) => {
            chapterIds.add(chapter.id);
          });
          setExpandedModules(chapterIds);
          
          // Auto-expand first chapter's sections for better UX
          if (data.contents[0].children && data.contents[0].children.length > 0) {
            const firstSection = data.contents[0].children[0];
            const sectionIds = new Set<string>();
            sectionIds.add(firstSection.id);
            setExpandedSections(sectionIds);
          }
        }
      } catch (err) {
        console.error('Failed to fetch book details:', err);
        setError(err instanceof Error ? err.message : 'Failed to load book content');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [bookId]);





  const handleThemeSelect = (theme: Theme) => {
    setSelectedTheme(theme);
    setIsThemeDialogOpen(false);
    // Here you would typically save the theme selection to your backend
    // bookService.updateBookTheme(bookId, theme.id);
  };

  







  // Toggle functions for expand/collapse
  const toggleModule = (moduleId: string) => {
    setExpandedModules(prev => {
      const newSet = new Set(prev);
      if (newSet.has(moduleId)) {
        newSet.delete(moduleId);
      } else {
        newSet.add(moduleId);
      }
      return newSet;
    });
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };



  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-8 w-64" />
            <Skeleton className="mt-2 h-4 w-96" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-24" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="md:col-span-2">
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-96 w-full" />
              </CardContent>
            </Card>
          </div>
          <div>
            <Card>
              <CardContent className="p-6">
                <Skeleton className="h-64 w-full" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (error || !book) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">{error || 'Book not found'}</h2>
          <p className="mt-2 text-gray-600">Please try again later</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-1">
        <div className="md:col-span-2">
          <Card>
            <CardContent className="p-6 pt-6">
              {/* Book structure header with edit button */}
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-xl font-semibold">Cấu trúc nội dung sách</h2>
                <Button variant="outline" onClick={openEditDialog} title="Chỉnh sửa" size="sm">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </Button>
              </div>

              {/* Book structure tree */}
              <BookContentDisplay
                contents={book.contents || []}
                onToggleModule={toggleModule}
                onToggleSection={toggleSection}
                expandedModules={expandedModules}
                expandedSections={expandedSections}
                getDeliveryMethodStyle={getDeliveryMethodStyle}
                getDeliveryMethodIcon={getDeliveryMethodIcon}
                getModuleTypeLabel={getModuleTypeLabel}
                getDeliveryMethodLabel={getDeliveryMethodLabel}
              />
            </CardContent>
          </Card>
        </div>

        <div className="hidden">
          {/* Book details card */}
          <Card>
            <CardContent className="p-4 pt-4">
              <h2 className="mb-4 text-lg font-semibold">
                {t("book-content.detail.bookDetails")}
              </h2>
              <dl className="space-y-2">
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("book-content.detail.status")}
                  </dt>
                  <dd>{book.status || "draft"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("book-content.detail.createdBy")}
                  </dt>
                  <dd className="text-sm">{book.createdBy || book.author || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("book-content.detail.createdAt")}
                  </dt>
                  <dd className="text-sm">{book.createdAt || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {t("book-content.detail.lastUpdated")}
                  </dt>
                  <dd className="text-sm">{book.updatedAt || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Duration</dt>
                  <dd className="text-sm">{book.duration || "-"} hours</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Level</dt>
                  <dd className="text-sm">{book.level || "-"}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Book Type
                  </dt>
                  <dd className="text-sm">{book.book_type || "-"}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>

          {/* Theme Selection and Preview Card */}
          <div className="mt-6 hidden">
            <ThemeSection
              selectedTheme={selectedTheme}
              onThemeChange={() => setIsThemeDialogOpen(true)}
            />
          </div>

          {/* Action buttons card */}
          <div className="mt-6 hidden">
            <Card>
              <CardContent className="p-6">
                <h2 className="mb-4 text-lg font-semibold">{t("book-content.detail.actions")}</h2>
                <div className="grid grid-cols-2 gap-2">
                  <Link to={`/lcms/book-content/${bookId}/composer`}>
                    <Button variant="outline" size="sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                      {t("book-content.detail.edit")}
                    </Button>
                  </Link>
                  <Button variant="outline" size="sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                      <circle cx="12" cy="12" r="3"></circle>
                    </svg>
                    {t("book-content.detail.preview")}
                  </Button>
                  <Button variant="outline" size="sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                      <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    </svg>
                    {t("book-content.detail.copy")}
                  </Button>
                  <Button variant="outline" size="sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    {t("book-content.detail.export")}
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-2">
                  {book.status === "draft" && (
                    <Button variant="primary" size="sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                        <polyline points="16 6 12 2 8 6"></polyline>
                        <line x1="12" y1="2" x2="12" y2="15"></line>
                      </svg>
                      {t("book-content.detail.sendForApproval")}
                    </Button>
                  )}

                  {book.status === "pending_approval" && (
                    <Button variant="primary" size="sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      {t("book-content.detail.approve")}
                    </Button>
                  )}

                  {book.status === "approved" && (
                    <Button variant="primary" size="sm">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="mr-2"
                      >
                        <path d="M3 15v4c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-4M17 9l-5 5-5-5M12 12.8V2.5"></path>
                      </svg>
                      {t("book-content.detail.publish")}
                    </Button>
                  )}

                  <Button variant="outline" size="sm">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mr-2"
                    >
                      <circle cx="12" cy="12" r="10"></circle>
                      <line x1="15" y1="9" x2="9" y2="15"></line>
                      <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                    {t("book-content.detail.cancel")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <ThemeSelectionDialog
        isOpen={isThemeDialogOpen}
        onClose={() => setIsThemeDialogOpen(false)}
        onSelectTheme={handleThemeSelect}
        currentTheme={selectedTheme || undefined}
      />

      {isEditDialogOpen && (
        <FullscreenBookEditDialog 
          bookId={bookId} 
          onClose={closeEditDialog}
        />
      )}
    </div>
  );
} 